import axios from 'axios';

// Detectar baseURL dinamicamente baseado no ambiente
const getApiUrl = () => {
  // Em produção, usar o mesmo domínio
  if (process.env.NODE_ENV === 'production' || window.location.protocol === 'https:') {
    return `${window.location.origin}/api`;
  }
  // Em desenvolvimento, usar localhost
  return process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
};

const API_URL = getApiUrl();

// Why-incidente 2026-09-22: timeout era 10s e o painel culpava a rede do usuário
// ("Verifique sua conexão") em qualquer falha. A causa real era o servidor levando
// >10s pra responder — a conexão do usuário estava intacta. 20s dá folga pra picos
// sem deixar ninguém esperando eternamente; a mensagem honesta sai de
// describeRequestError().
const api = axios.create({
  baseURL: API_URL,
  timeout: 20000,
});

// Traduz um erro de request na mensagem que o usuário merece ver. Distinguir
// timeout de queda de rede importa: são ações diferentes (esperar/tentar de novo
// vs. checar a internet), e chamar tudo de "sua conexão" manda o usuário caçar
// um problema que não é dele.
export function describeRequestError(error) {
  // Axios marca timeout como ECONNABORTED (ou ETIMEDOUT em versões novas).
  if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT' ||
      /timeout/i.test(error?.message || '')) {
    return {
      kind: 'timeout',
      message: 'O servidor demorou demais para responder. Sua conexão está ok — tentando de novo...'
    };
  }
  // Sem response e sem código de timeout: request não chegou ao servidor.
  if (error?.code === 'ERR_NETWORK' || (!error?.response && error?.request)) {
    return {
      kind: 'offline',
      message: navigator.onLine === false
        ? 'Você está sem internet. Reconecte para continuar.'
        : 'Não foi possível falar com o servidor. Ele pode estar fora do ar.'
    };
  }
  const status = error?.response?.status;
  if (status >= 500) {
    return { kind: 'server', message: 'O servidor encontrou um erro ao carregar as conversas.' };
  }
  if (status === 403) {
    return { kind: 'forbidden', message: 'Você não tem permissão para ver estas conversas.' };
  }
  return {
    kind: 'unknown',
    message: error?.response?.data?.message || 'Não foi possível carregar as conversas.'
  };
}

// Interceptor para adicionar token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Centraliza o logout — limpa tokens e redireciona. Evita o estado zumbi
// "logado mas sem token" que fazia a lista de conversas ficar vazia em
// silêncio (sem mensagem, sem redirect), exigindo que o usuário limpasse
// dados do navegador pra voltar a funcionar.
function forceLogout() {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    // 'user' também sai — senão o app pode renderizar UI logada por 1 frame
  } catch (e) {}
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

// Interceptor para refreshToken
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;

    // Rota de refresh falhando — não tenta refrescar de novo (loop infinito).
    const isRefreshCall = (originalRequest.url || '').includes('/auth/refresh-token');

    // 403 "Token não fornecido" — frontend perdeu o token mas continua tentando.
    // Sai pra login em vez de propagar erro silencioso.
    if (status === 403 && !localStorage.getItem('token') && !isRefreshCall) {
      forceLogout();
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry && !isRefreshCall) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refreshToken');

      // Sem refresh token → não há como recuperar. Faz logout imediato em vez
      // de propagar 401 silencioso (que deixava o painel zumbi até o usuário
      // limpar dados do navegador).
      if (!refreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      try {
        const response = await api.post('/auth/refresh-token', { refreshToken });
        localStorage.setItem('token', response.data.token);
        // Atualiza o user em cache também — o objeto antigo pode ter sido
        // gravado por uma versão buggy do app, com campos faltando.
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
        return api(originalRequest);
      } catch (refreshError) {
        forceLogout();
        return Promise.reject(refreshError);
      }
    }

    // 401 numa request já retentada → refresh não resolveu, sai pra login.
    if (status === 401 && originalRequest._retry && !isRefreshCall) {
      forceLogout();
    }

    return Promise.reject(error);
  }
);

export default api;
